<script setup lang="ts">
/**
 * „Meine Designs“: alle (nicht archivierten) Designs des Projekts – öffnen, umbenennen,
 * duplizieren, löschen, neu anlegen. Postkarten-Rückseiten erscheinen nicht separat.
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { Copy, FolderOpen, ImageOff, LoaderCircle, Lock, Pencil, Plus, Trash2 } from 'lucide-vue-next'
import { deleteDesign, duplicateDesign, listDesigns, renameDesign, type DesignDocumentPayload } from '../../../services/designApi'
import { useDesignStore } from '../../../store/useDesignStore'
import { getWidgetConfig } from '../../../runtime/widgetConfig'
import { useToast } from '../../../composables/useToast'

const props = defineProps<{ productTitles: Record<string, string> }>()
const emit = defineEmits<{
  (e: 'open', design: { id: string; productId: number; categoryId: number }): void
  (e: 'new'): void
}>()

const store = useDesignStore()
const toast = useToast()
const editorBaseUrl = () => String((getWidgetConfig() as any)?.editorBaseUrl || '')

const items = ref<DesignDocumentPayload[]>([])
const loading = ref(false)
const error = ref('')
const busyId = ref<string | null>(null)
const renamingId = ref<string | null>(null)
const renameDraft = ref('')
const confirmDeleteId = ref<string | null>(null)
const renameInput = ref<HTMLInputElement[] | HTMLInputElement | null>(null)

const currentId = computed(() => store.meta?.id ?? null)
const visible = computed(() => items.value.filter((d) => d.side !== 'back'))

async function load() {
  if (!store.projectCode) return
  loading.value = true
  error.value = ''
  try {
    const res = await listDesigns({ editorBaseUrl: editorBaseUrl(), projectCode: store.projectCode })
    items.value = res.items
  } catch (e: any) {
    error.value = e?.error || 'Designs konnten nicht geladen werden.'
  } finally {
    loading.value = false
  }
}
onMounted(load)
watch(currentId, load)
// Name im Kopf geändert -> Liste mitziehen
watch(() => store.designName, (name) => {
  const id = currentId.value
  if (id) items.value = items.value.map((d) => (d.id === id ? { ...d, name: name || null } : d))
})

function productTitle(d: DesignDocumentPayload) {
  return props.productTitles[String(d.productId)] || 'Design'
}
function label(d: DesignDocumentPayload) {
  return d.name?.trim() || productTitle(d)
}
function dateLabel(iso: string) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000)
  if (days <= 0) return `heute, ${date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`
  if (days === 1) return 'gestern'
  return date.toLocaleDateString('de-DE')
}
function thumb(d: DesignDocumentPayload) {
  return d.thumbnailUrl || d.previewUrl || ''
}

function open(d: DesignDocumentPayload) {
  if (d.id === currentId.value || busyId.value) return
  emit('open', { id: d.id, productId: d.productId, categoryId: d.categoryId })
}

async function startRename(d: DesignDocumentPayload) {
  confirmDeleteId.value = null
  renamingId.value = d.id
  renameDraft.value = d.name || ''
  await nextTick()
  const el = Array.isArray(renameInput.value) ? renameInput.value[0] : renameInput.value
  el?.focus()
  el?.select()
}
async function commitRename(d: DesignDocumentPayload) {
  if (renamingId.value !== d.id) return
  renamingId.value = null
  const name = renameDraft.value.replace(/\s+/g, ' ').trim()
  if (name === (d.name || '')) return
  busyId.value = d.id
  try {
    if (d.id === currentId.value) {
      await store.rename(name)
    } else {
      const res = await renameDesign({ editorBaseUrl: editorBaseUrl(), projectCode: store.projectCode, id: d.id, name: name || null })
      items.value = items.value.map((x) => (x.id === d.id ? { ...x, name: res.name } : x))
    }
  } catch {
    toast.error('Der Name konnte nicht gespeichert werden.')
  } finally {
    busyId.value = null
  }
}

async function duplicate(d: DesignDocumentPayload) {
  busyId.value = d.id
  try {
    if (d.id === currentId.value) await store.flushSave()
    const res = await duplicateDesign({ editorBaseUrl: editorBaseUrl(), projectCode: store.projectCode, id: d.id })
    toast.success('Kopie angelegt')
    emit('open', { id: res.document.id, productId: res.document.productId, categoryId: res.document.categoryId })
  } catch {
    toast.error('Das Design konnte nicht kopiert werden.')
  } finally {
    busyId.value = null
  }
}

async function remove(d: DesignDocumentPayload) {
  if (confirmDeleteId.value !== d.id) {
    renamingId.value = null
    confirmDeleteId.value = d.id
    return
  }
  confirmDeleteId.value = null
  busyId.value = d.id
  try {
    await deleteDesign({ editorBaseUrl: editorBaseUrl(), projectCode: store.projectCode, id: d.id })
    items.value = items.value.filter((x) => x.id !== d.id && x.linkedDocumentId !== d.id)
    toast.success(`„${label(d)}“ gelöscht`)
  } catch (e: any) {
    toast.error(e?.status === 423 ? 'Bestellte Designs können nicht gelöscht werden.' : 'Das Design konnte nicht gelöscht werden.')
  } finally {
    busyId.value = null
  }
}
</script>

<template>
  <div class="myd">
    <button type="button" class="myd__new" @click="emit('new')">
      <Plus :size="16" /> Neues Design beginnen
    </button>

    <p v-if="loading && !items.length" class="myd__hint"><LoaderCircle :size="14" class="myd__spin" /> Designs werden geladen…</p>
    <p v-else-if="error" class="myd__hint myd__hint--err">{{ error }}</p>
    <p v-else-if="!visible.length" class="myd__hint">Noch keine gespeicherten Designs.</p>

    <ul v-else class="myd__list">
      <li
        v-for="d in visible"
        :key="d.id"
        class="myd__item"
        :class="{ 'myd__item--current': d.id === currentId, 'myd__item--busy': busyId === d.id }"
      >
        <button type="button" class="myd__thumb" :disabled="d.id === currentId" :title="d.id === currentId ? 'Gerade geöffnet' : `„${label(d)}“ öffnen`" @click="open(d)">
          <img v-if="thumb(d)" :src="thumb(d)" alt="" loading="lazy" />
          <ImageOff v-else :size="20" />
        </button>

        <div class="myd__body">
          <input
            v-if="renamingId === d.id"
            ref="renameInput"
            v-model="renameDraft"
            class="myd__rename"
            type="text"
            maxlength="120"
            :placeholder="productTitle(d)"
            aria-label="Name des Designs"
            @keydown.enter.prevent="commitRename(d)"
            @keydown.esc.prevent="renamingId = null"
            @blur="commitRename(d)"
          />
          <button v-else type="button" class="myd__name" :class="{ 'myd__name--unnamed': !d.name }" @click="open(d)">{{ label(d) }}</button>
          <span class="myd__meta">
            <template v-if="d.name">{{ productTitle(d) }} · </template>{{ dateLabel(d.updatedAt) }}
          </span>
          <span v-if="d.id === currentId" class="myd__badge myd__badge--current">Geöffnet</span>
          <span v-else-if="d.status === 'ordered'" class="myd__badge"><Lock :size="11" /> Bestellt</span>

          <div v-if="confirmDeleteId === d.id" class="myd__confirm">
            <span>Wirklich löschen?</span>
            <button type="button" class="myd__confirm-yes" @click="remove(d)">Löschen</button>
            <button type="button" class="myd__confirm-no" @click="confirmDeleteId = null">Abbrechen</button>
          </div>
          <div v-else class="myd__actions">
            <button v-if="d.id !== currentId" type="button" class="myd__act" title="Öffnen" aria-label="Öffnen" @click="open(d)"><FolderOpen :size="15" /></button>
            <button type="button" class="myd__act" title="Umbenennen" aria-label="Umbenennen" @click="startRename(d)"><Pencil :size="15" /></button>
            <button type="button" class="myd__act" title="Kopie anlegen" aria-label="Kopie anlegen" @click="duplicate(d)"><Copy :size="15" /></button>
            <button
              v-if="d.id !== currentId && d.status !== 'ordered'"
              type="button"
              class="myd__act myd__act--danger"
              title="Löschen"
              aria-label="Löschen"
              @click="remove(d)"
            ><Trash2 :size="15" /></button>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.myd { display: grid; gap: 12px; }
.myd__new { display: inline-flex; align-items: center; justify-content: center; gap: 8px; height: 40px; border: 1px dashed rgba(15, 23, 42, 0.28); border-radius: 6px; background: #fff; color: #0f172a; font: inherit; font-size: 13px; font-weight: 700; cursor: pointer; }
.myd__new:hover { border-color: var(--pfe-color-accent, #e30b17); color: var(--pfe-color-accent, #e30b17); }
.myd__hint { display: flex; align-items: center; gap: 6px; margin: 0; font-size: 13px; color: rgba(15, 23, 42, 0.6); }
.myd__hint--err { color: #b91c1c; }
.myd__spin { animation: myd-spin 0.8s linear infinite; }
@keyframes myd-spin { to { transform: rotate(360deg); } }
.myd__list { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
.myd__item { display: flex; gap: 10px; padding: 8px; border: 1px solid rgba(15, 23, 42, 0.1); border-radius: 6px; background: #fff; transition: border-color 0.15s ease, opacity 0.15s ease; }
.myd__item:hover { border-color: rgba(15, 23, 42, 0.22); }
.myd__item--current { border-color: var(--pfe-color-accent, #e30b17); box-shadow: 0 1px 4px rgba(227, 11, 23, 0.12); }
.myd__item--busy { opacity: 0.55; pointer-events: none; }
.myd__thumb { flex: 0 0 64px; height: 64px; display: inline-flex; align-items: center; justify-content: center; padding: 0; border: 1px solid rgba(15, 23, 42, 0.08); border-radius: 4px; background: #f8fafc; color: rgba(15, 23, 42, 0.35); overflow: hidden; cursor: pointer; }
.myd__thumb:disabled { cursor: default; }
.myd__thumb img { width: 100%; height: 100%; object-fit: contain; }
.myd__body { position: relative; flex: 1 1 auto; min-width: 0; display: grid; align-content: start; gap: 2px; }
.myd__name { padding: 0; border: 0; background: none; font: inherit; font-size: 13px; font-weight: 700; color: #0f172a; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer; padding-right: 64px; }
.myd__name--unnamed { font-weight: 600; color: rgba(15, 23, 42, 0.72); }
.myd__rename { height: 26px; padding: 0 6px; border: 1px solid var(--pfe-color-accent, #e30b17); border-radius: 4px; font: inherit; font-size: 13px; font-weight: 700; outline: none; box-shadow: 0 0 0 3px rgba(227, 11, 23, 0.12); }
.myd__meta { font-size: 12px; color: rgba(15, 23, 42, 0.58); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.myd__badge { position: absolute; top: 0; right: 0; display: inline-flex; align-items: center; gap: 3px; padding: 1px 6px; border-radius: 999px; background: rgba(15, 23, 42, 0.06); font-size: 11px; font-weight: 600; color: rgba(15, 23, 42, 0.7); }
.myd__badge--current { background: rgba(227, 11, 23, 0.08); color: var(--pfe-color-accent, #e30b17); }
.myd__actions { display: flex; gap: 2px; margin-top: 4px; }
.myd__act { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 28px; border: 0; border-radius: 4px; background: transparent; color: rgba(15, 23, 42, 0.62); cursor: pointer; }
.myd__act:hover { background: rgba(15, 23, 42, 0.06); color: #0f172a; }
.myd__act--danger:hover { background: rgba(220, 38, 38, 0.08); color: #b91c1c; }
.myd__confirm { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 4px; font-size: 12px; color: #b91c1c; font-weight: 600; }
.myd__confirm button { height: 26px; padding: 0 10px; border-radius: 4px; font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; }
.myd__confirm-yes { border: 1px solid #dc2626; background: #dc2626; color: #fff; }
.myd__confirm-no { border: 1px solid rgba(15, 23, 42, 0.16); background: #fff; color: #0f172a; }
</style>
